const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

/**
 * Get event-level capacity and tickets sold (across all tiers + packs).
 * Returns { max_capacity, tickets_sold, tickets_remaining } or null on failure.
 * tickets_remaining is null when the event has no max_capacity set.
 */
export async function getEventAvailability(eventId) {
  try {
    const res = await fetch(`${API_URL}/ticketing/tiers/availability?event_id=${eventId}`);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

/**
 * Get available ticket tiers for an event.
 * Passes customer_token if available to show member-only tiers.
 */
export async function getEventTiers(eventId, customerToken = null) {
  const params = new URLSearchParams({ event_id: eventId });
  if (customerToken) params.append("customer_token", customerToken);

  const res = await fetch(`${API_URL}/ticketing/tiers?${params}`);
  if (!res.ok) throw new Error("Failed to load ticket tiers");
  return res.json();
}

/**
 * Get active ticket packs for an event (bundles of N tickets at a fixed price).
 */
export async function getEventPacks(eventId) {
  try {
    const res = await fetch(`${API_URL}/ticketing/tiers/packs?event_id=${eventId}`);
    if (!res.ok) return [];
    return res.json();
  } catch {
    return [];
  }
}

/**
 * Get active promotions for an event (buy x get y free).
 */
export async function getEventPromotions(eventId) {
  try {
    const res = await fetch(`${API_URL}/ticketing/tiers/promotions?event_id=${eventId}`);
    if (!res.ok) return [];
    return res.json();
  } catch {
    return [];
  }
}

/**
 * Create a reservation (starts checkout).
 * For free events: returns confirmed tickets immediately.
 * For paid events: returns a checkout_url to redirect to Stripe.
 */
export async function createReservation({
  eventId,
  items,
  packItems,
  customerToken,
  guestEmail,
  guestName,
  guestPhone,
  successUrl,
  cancelUrl,
  promoCode,
  paymentMethod = "stripe",
}) {
  const body = {
    event_id: eventId,
    items,
    success_url: successUrl,
    cancel_url: cancelUrl,
    payment_method: paymentMethod,
  };
  if (packItems && packItems.length > 0) body.pack_items = packItems;

  if (customerToken) body.customer_token = customerToken;
  if (guestEmail) body.guest_email = guestEmail;
  if (guestName) body.guest_name = guestName;
  if (guestPhone) body.guest_phone_e164 = guestPhone;
  if (promoCode) body.promo_code = promoCode;

  const res = await fetch(`${API_URL}/ticketing/reservations`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to create reservation");
  }
  return res.json();
}

/**
 * Charge a pending reservation via the customer's saved Venmo vault.
 * The reservation must have been created with payment_method="venmo".
 */
export async function venmoChargeReservation(reservationId, customerToken) {
  const res = await fetch(`${API_URL}/ticketing/reservations/${reservationId}/venmo-charge`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customer_token: customerToken }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Venmo payment failed");
  }
  return res.json();
}

/**
 * Validate a promo code and get the discount amount for the current cart.
 * Returns { valid, code_id, code, discount_cents, discount_type, discount_value, description, message }
 * Throws with a user-facing message on invalid/expired codes.
 */
export async function validatePromoCode({ code, eventId, items, customerToken = null }) {
  const body = { code, event_id: eventId, items };
  if (customerToken) body.customer_token = customerToken;

  const res = await fetch(`${API_URL}/ticketing/promo-codes/validate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Invalid promo code");
  }
  return res.json();
}

/**
 * Get reservation detail by ID.
 */
export async function getReservation(reservationId) {
  const res = await fetch(`${API_URL}/ticketing/reservations/${reservationId}`);
  if (!res.ok) throw new Error("Reservation not found");
  return res.json();
}

/**
 * Get all tickets for a loyalty customer.
 */
export async function getMyTickets(customerToken) {
  const res = await fetch(
    `${API_URL}/ticketing/tickets?customer_token=${encodeURIComponent(customerToken)}`
  );
  if (!res.ok) throw new Error("Failed to load tickets");
  return res.json();
}

/**
 * Verify payment directly with Stripe via the backend (webhook fallback).
 * Call this on the confirmation page when session_id is in the URL and the
 * reservation is still pending — handles production webhook delivery issues.
 */
export async function verifyPayment(reservationId, sessionId) {
  const res = await fetch(
    `${API_URL}/ticketing/reservations/${reservationId}/verify-payment`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Verification failed");
  }
  return res.json();
}

/**
 * Request a full refund for a paid reservation.
 * Pass customerToken for loyalty accounts, or guestEmail for guest reservations.
 */
export async function requestRefund(reservationId, { customerToken = null, guestEmail = null } = {}) {
  const params = new URLSearchParams();
  if (customerToken) params.append("customer_token", customerToken);
  if (guestEmail) params.append("guest_email", guestEmail);

  const res = await fetch(
    `${API_URL}/ticketing/reservations/${reservationId}/refund?${params}`,
    { method: "POST" }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Refund request failed");
  }
  return res.json();
}

/**
 * Check whether a ticket is eligible for a customer-initiated refund.
 * Uses fields returned in TicketResponse: reservation_payment_status, refund_cutoff_days, start_at, status.
 */
export function isRefundEligible(ticket) {
  if (!ticket) return false;
  if (ticket.status !== "valid") return false;
  if (ticket.reservation_payment_status !== "paid") return false;
  if (ticket.refund_cutoff_days === null || ticket.refund_cutoff_days === undefined) return false;
  if (!ticket.start_at) return false;
  const daysUntil = (new Date(ticket.start_at) - new Date()) / (1000 * 60 * 60 * 24);
  return daysUntil > ticket.refund_cutoff_days;
}

/**
 * Get a single ticket by its UUID code.
 */
export async function getTicketByCode(ticketCode) {
  const res = await fetch(`${API_URL}/ticketing/tickets/${ticketCode}`);
  if (!res.ok) throw new Error("Ticket not found");
  return res.json();
}

// ─── Tier type helpers ────────────────────────────────────────────────────────
//
// Tier classification by sale window:
//   Early Bird — has sale_ends_at, no sale_starts_at
//   GA         — has both sale_starts_at AND sale_ends_at (opens after early bird)
//   Door       — has sale_starts_at, no sale_ends_at (event-day purchase)
//   Member     — is_member_only = true

const isEarlyBirdTier = (t) => !!t.sale_ends_at && !t.sale_starts_at;
const isDoorTier      = (t) => !!t.sale_starts_at && !t.sale_ends_at;

/**
 * Infer the badge label for a tier.
 * Used in the UI to show chips like "Early Bird", "At the Door", "Member Price".
 */
export function getTierTypeBadge(tier) {
  if (tier.is_member_only) return { label: "Member Price", color: "member" };
  if (tier.price_cents === 0) return { label: "Free", color: "free" };
  if (isEarlyBirdTier(tier) && new Date(tier.sale_ends_at) > new Date()) {
    return { label: "Early Bird", color: "early-bird" };
  }
  if (isDoorTier(tier) && tier.sale_status === "on_sale") {
    return { label: "At the Door", color: "door" };
  }
  return null;
}

/**
 * Format cents as a dollar string: 1500 → "$15.00", 0 → "FREE"
 */
export function formatPrice(cents) {
  if (cents === 0) return "FREE";
  return `$${(cents / 100).toFixed(2)}`;
}

// ─── Active tier price helpers ────────────────────────────────────────────────
//
// Priority: Door (event day) > Early Bird > GA
// Only one tier type is purchasable at a time.

function getOnSaleTiers(tiers) {
  const now = new Date();
  const regular = (tiers || []).filter((t) => !t.is_member_only && t.is_active !== false);

  const hasActiveDoor = regular.some((t) => isDoorTier(t) && t.sale_status === "on_sale");
  const hasActiveEarlyBird = regular.some(
    (t) => isEarlyBirdTier(t) && new Date(t.sale_ends_at) > now && t.price_cents > 0
  );

  return regular.filter((t) => {
    if (t.sale_status !== "on_sale") return false;
    if (t.price_cents === 0) return true;
    if (hasActiveDoor) return isDoorTier(t);
    if (hasActiveEarlyBird) return isEarlyBirdTier(t) && new Date(t.sale_ends_at) > now;
    return !isDoorTier(t) && (!isEarlyBirdTier(t) || new Date(t.sale_ends_at) > now);
  });
}

/**
 * Returns the lowest display price (in cents) for the current active tier,
 * with member discount applied if the user is authenticated as a member.
 *
 * Mirrors TierSelector: the member price is the active regular tier's price
 * with the member tier's discount applied — not the member tier's own price_cents.
 *
 * Returns 0 for free events, null if no tiers are on sale.
 */
export function getLowestDisplayPrice(tiers) {
  const onSale = getOnSaleTiers(tiers);
  if (onSale.length === 0) return null;

  // If the customer is a member (any member-only tier is unlocked), apply the discount
  // to the active regular tier price — same calculation as TierSelector.
  const memberTier = (tiers || []).find((t) => t.is_member_only && !t.member_locked);
  if (memberTier) {
    const applyDiscount = (priceCents) => {
      if (priceCents === 0) return 0;
      const dt = memberTier.member_discount_type;
      const dv = memberTier.member_discount_value || 0;
      if (dt === "set_price")   return memberTier.price_cents;
      if (dt === "percent_off") return Math.max(0, Math.round(priceCents * (1 - dv / 100)));
      if (dt === "amount_off")  return Math.max(0, priceCents - dv);
      return priceCents;
    };
    return Math.min(...onSale.map((t) => applyDiscount(t.price_cents)));
  }

  return Math.min(...onSale.map((t) => t.price_cents));
}

/**
 * Returns the lowest price (in cents) among currently purchasable regular tiers,
 * without member discounts. Used as a reference price for member discount calculations.
 *
 * Returns 0 for free events, null if no tiers are on sale.
 */
export function getLowestActiveTierPrice(tiers) {
  const onSale = getOnSaleTiers(tiers);
  if (onSale.length === 0) return null;
  return Math.min(...onSale.map((t) => t.price_cents));
}

/**
 * Returns true when every on-sale tier with a finite capacity is fully sold out.
 */
export function isEventSoldOut(tiers) {
  const onSale = getOnSaleTiers(tiers);
  if (onSale.length === 0) return false;
  return onSale.every((t) => t.tickets_available !== null && t.tickets_available <= 0);
}

/**
 * Returns the lowest remaining ticket count across on-sale capped tiers,
 * or null if all on-sale tiers have unlimited capacity.
 */
export function getLowestAvailableCount(tiers) {
  const onSale = getOnSaleTiers(tiers);
  const capped = onSale.filter((t) => t.tickets_available !== null && t.tickets_available > 0);
  if (capped.length === 0) return null;
  return Math.min(...capped.map((t) => t.tickets_available));
}

/**
 * Calculate total tax cents for an order.
 * Each tier can have its own tax_percent (e.g. 8.25 = 8.25%).
 * Member tiers inherit tax from the currently active regular tier.
 * Tax is applied after the discount: taxableAmount = (price * qty) * (1 - discountRatio)
 */
export function calculateOrderTax(tiers, quantities, discountCents = 0, totalSubtotalCents = 0) {
  const discountRatio = (discountCents > 0 && totalSubtotalCents > 0)
    ? Math.min(1, discountCents / totalSubtotalCents)
    : 0;

  const now = new Date();
  const regularPaid = tiers.filter((t) => !t.is_member_only && t.price_cents > 0);
  const activeDoor = regularPaid.find((t) => isDoorTier(t) && t.sale_status === "on_sale");
  const activeEarlyBird = regularPaid.find(
    (t) => isEarlyBirdTier(t) && new Date(t.sale_ends_at) > now
  );
  const activeRegularTier = activeDoor
    || activeEarlyBird
    || regularPaid.find((t) => !isDoorTier(t) && !isEarlyBirdTier(t));

  return tiers.reduce((sum, t) => {
    const qty = quantities[t.tier_id] || 0;
    if (qty === 0) return sum;
    const effectivePrice = getMemberEffectivePrice(t, tiers);
    let taxPercent = t.tax_percent || 0;
    if (t.is_member_only && !taxPercent) {
      taxPercent = activeRegularTier?.tax_percent || 0;
    }
    const taxableAmount = Math.round(effectivePrice * qty * (1 - discountRatio));
    return sum + Math.round(taxableAmount * (taxPercent / 100));
  }, 0);
}

/**
 * Get the effective (actual checkout) price for a tier.
 * For member tiers with percent_off or amount_off, applies the discount
 * against the current lowest non-member tier price.
 */
export function getMemberEffectivePrice(tier, allTiers) {
  const dt = tier.member_discount_type;
  if (!tier.is_member_only || !dt || dt === "set_price") {
    return tier.price_cents;
  }
  const refPrice = getLowestActiveTierPrice(allTiers) ?? 0;
  const dv = tier.member_discount_value || 0;
  if (dt === "percent_off") return Math.max(0, Math.round(refPrice * (1 - dv / 100)));
  return Math.max(0, refPrice - dv);
}
